import { css, html, nothing } from 'lit';
import { api } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import { shortFingerprint } from '../../lib/fingerprint.js';
import { formatMediumDate } from '../../lib/format.js';
import {
    MSA_AWAITING_COUNTERSIGNATURE,
    MSA_AWAITING_SIGNATURE,
    MSA_EXECUTED,
    MSA_STATUS_COLORS,
    MSA_STATUS_LABELS,
} from '../../lib/msa.js';
import {
    requiresSigningDate,
    TAX_FORM_OPTIONS,
    TAX_FORM_W8BEN,
    taxFormSummary,
} from '../../lib/tax-form.js';
import { uploadFile } from '../../lib/upload.js';

class FreelancersView extends BaseComponent {
    static properties = {
        _freelancers: { type: Array, state: true },
        _invitations: { type: Array, state: true },
        _loading: { type: Boolean, state: true },
        _error: { type: String, state: true },
        _view: { type: String, state: true }, // 'list', 'invite', 'contract', 'external-sow', 'msa'
        _msaFor: { type: Object, state: true }, // freelancer whose MSA is open
        _msa: { type: Object, state: true }, // GET /api/freelancers/:id/msa
        _countersign: { type: Object, state: true },
        _sowFor: { type: Object, state: true }, // freelancer whose paper SOW is being recorded
        _sow: { type: Object, state: true },
        _sows: { type: Array, state: true }, // SOWs on file for _sowFor
        _contractFor: { type: Object, state: true }, // freelancer whose paperwork is being edited
        _contract: { type: Object, state: true },
        _inviteEmail: { type: String, state: true },
        _inviteMessage: { type: String, state: true },
        _inviteContract: { type: Object, state: true },
        _saving: { type: Boolean, state: true },
        _removingId: { type: String, state: true }, // local users.id awaiting confirmation
    };

    static styles = css`
        :host { display: block; }
        .page-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem; }
        .page-header h4 { margin: 0; font-size: 1.5rem; font-weight: 600; }
        .page-subtitle { color: #666; font-size: 0.9rem; margin-bottom: 1.5rem; }
        table { font-size: 0.9rem; }
        .section-title { font-weight: 600; font-size: 1.1rem; margin-top: 2rem; margin-bottom: 0.75rem; }
        .section-title:first-of-type { margin-top: 0; }
        .back-link {
            display: inline-flex; align-items: center; gap: 0.25rem;
            color: #666; text-decoration: none; font-size: 0.9rem; margin-bottom: 1rem;
        }
        .back-link:hover { color: #333; }
        .form-panel { max-width: 500px; }
        .status-badge { font-size: 0.75rem; }
        .confirm-prompt { color: #666; font-size: 0.85rem; margin-right: 0.5rem; }
        .paperwork { font-size: 0.85rem; }
        .paperwork-sub { font-size: 0.75rem; }
        .paperwork-missing { color: #dc3545; font-size: 0.8rem; }
        .form-hint { color: #666; font-size: 0.85rem; margin-bottom: 1rem; }
    `;

    constructor() {
        super();
        this._freelancers = [];
        this._invitations = [];
        this._loading = true;
        this._error = null;
        this._view = 'list';
        this._inviteEmail = '';
        this._inviteMessage = '';
        this._inviteContract = {};
        this._saving = false;
        this._removingId = null;
        this._contractFor = null;
        this._contract = {};
        this._sowFor = null;
        this._sow = {};
        this._sows = [];
        this._msaFor = null;
        this._msa = null;
        this._countersign = {};
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadData();
    }

    async _loadData() {
        this._loading = true;
        this._error = null;
        try {
            const [freelancersData, invitationsData] = await Promise.all([
                api.get('/api/freelancers'),
                api.get('/api/freelancers/invitations'),
            ]);
            this._freelancers = freelancersData.freelancers || [];
            this._invitations = invitationsData.invitations || [];
        } catch (err) {
            this._error = err.message;
        } finally {
            this._loading = false;
        }
    }

    _startInvite() {
        this._view = 'invite';
        this._inviteEmail = '';
        this._inviteMessage = '';
        this._error = null;
    }

    _backToList() {
        this._view = 'list';
        this._error = null;
    }

    async _submitInvite(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            const body = { email: this._inviteEmail };
            if (this._inviteMessage) {
                body.message = this._inviteMessage;
            }
            // The MSA is normally signed in the app after the invitation; its
            // fields go only when staff are recording one signed outside it.
            const outside = !!this._inviteContract.signed_outside;
            await api.post('/api/freelancers/invitations', {
                ...body,
                legal_name: outside ? this._inviteContract.legal_name || '' : '',
                msa_reference: outside ? this._inviteContract.msa_reference || null : null,
                msa_signed_on: outside ? this._inviteContract.msa_signed_on || null : null,
                msa_upload_id: outside ? this._inviteContract.msa_upload_id || null : null,
                tax_form: this._inviteContract.tax_form || TAX_FORM_W8BEN,
                tax_form_on_file: !!this._inviteContract.tax_form_on_file,
                tax_form_signed_on: this._inviteContract.tax_form_signed_on || null,
            });
            this._inviteContract = {};
            this._view = 'list';
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _revokeInvitation(invitation) {
        this._error = null;
        try {
            await api.delete(`/api/freelancers/invitations/${invitation.id}`);
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        }
    }

    async _removeFreelancer(freelancer) {
        this._error = null;
        try {
            await api.delete(`/api/freelancers/${freelancer.id}`);
            this._removingId = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        }
    }

    _startContract(freelancer) {
        this._contractFor = freelancer;
        // Seed the form from what is on record so an edit is a correction, not
        // a re-entry.
        this._contract = { ...(freelancer.contract || {}) };
        this._error = null;
        this._view = 'contract';
    }

    async _startMSA(freelancer) {
        this._msaFor = freelancer;
        this._msa = null;
        this._countersign = { consent: false, typed_signature: '', title: '' };
        this._error = null;
        this._view = 'msa';
        try {
            this._msa = await api.get(`/api/freelancers/${freelancer.id}/msa`);
        } catch (err) {
            this._error = err.message;
        }
    }

    async _countersignMSA(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.post(`/api/freelancers/${this._msaFor.id}/msa/countersign`, {
                document_sha256: this._msa.signing.document_sha256,
                consent: this._countersign.consent,
                typed_signature: this._countersign.typed_signature,
                title: this._countersign.title,
            });
        } catch (err) {
            this._error = err.message;
            this._saving = false;
            return;
        }
        this._saving = false;
        await this._loadData();
        await this._startMSA(
            this._freelancers.find((f) => f.id === this._msaFor.id) || this._msaFor,
        );
    }

    async _voidMSA() {
        this._saving = true;
        this._error = null;
        try {
            await api.post(`/api/freelancers/${this._msaFor.id}/msa/void`, {});
        } catch (err) {
            this._error = err.message;
            this._saving = false;
            return;
        }
        this._saving = false;
        this._view = 'list';
        await this._loadData();
    }

    async _downloadMSA(freelancer) {
        this._error = null;
        try {
            const { url } = await api.get(`/api/freelancers/${freelancer.id}/msa/download`);
            window.open(url, '_blank', 'noopener');
        } catch (err) {
            this._error = err.message;
        }
    }

    // Recording an SOW signed on paper keeps the per-contractor numbering
    // continuous. It matters more than it looks: NextSOWNumber is MAX+1, so an
    // unrecorded paper SOW No. 1 makes the next system SOW call itself No. 1 and
    // re-charge the onboarding fee the contractor was already paid.
    async _startExternalSOW(freelancer) {
        this._sowFor = freelancer;
        this._sow = {};
        this._sows = [];
        this._error = null;
        this._view = 'external-sow';
        try {
            const data = await api.get(`/api/freelancers/${freelancer.id}/sows`);
            this._sows = data.sows || [];
        } catch {
            // Staff can still record one without the existing list; the server
            // rejects a duplicate number either way.
            this._sows = [];
        }
    }

    async _uploadSOWDocument(e) {
        const file = e.target.files?.[0];
        if (!file) return;
        this._saving = true;
        this._error = null;
        try {
            const upload = await uploadFile(file);
            this._sow = {
                ...this._sow,
                upload_id: upload.upload_id,
                file_name: file.name,
            };
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _saveExternalSOW(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            // Form-encoded, not JSON — the endpoint reads FormValue.
            const form = new FormData();
            form.append('upload_id', this._sow.upload_id);
            if (this._sow.number) {
                form.append('number', String(this._sow.number));
            }
            await api.upload(`/api/freelancers/${this._sowFor.id}/sows/external`, form);
            this._view = 'list';
            this._sowFor = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _saveContract(e) {
        e.preventDefault();
        this._saving = true;
        this._error = null;
        try {
            await api.put(`/api/freelancers/${this._contractFor.id}/contract`, {
                legal_name: this._contract.legal_name || '',
                msa_reference: this._contract.msa_reference || null,
                msa_signed_on: this._contract.msa_signed_on || null,
                msa_upload_id: this._contract.msa_upload_id || null,
                tax_form: this._contract.tax_form || TAX_FORM_W8BEN,
                tax_form_on_file: !!this._contract.tax_form_on_file,
                tax_form_signed_on: this._contract.tax_form_signed_on || null,
            });
            this._view = 'list';
            this._contractFor = null;
            await this._loadData();
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    async _uploadMSA(e, target) {
        const file = e.target.files?.[0];
        if (!file) return;
        this._saving = true;
        this._error = null;
        try {
            const upload = await uploadFile(file);
            this[target] = {
                ...this[target],
                msa_upload_id: upload.upload_id,
                msa_file_name: file.name,
            };
        } catch (err) {
            this._error = err.message;
        } finally {
            this._saving = false;
        }
    }

    _renderMSAUpload(target, required = false) {
        const current = this[target];
        return html`
            <div class="mb-3">
                <label class="form-label">Signed master services agreement (PDF)${required ? html` <span class="text-danger">*</span>` : ''}</label>
                ${
                    current.msa_upload_id
                        ? html`<div class="form-text text-success">Attached${current.msa_file_name ? html` \u2014 ${current.msa_file_name}` : ''}</div>`
                        : html`<input type="file" class="form-control" accept="application/pdf"
                            @change="${(e) => this._uploadMSA(e, target)}" />`
                }
            </div>
        `;
    }

    // Summarises what is on record: whether an SOW could be signed at all (legal
    // name) and where the contractor's tax form stands.
    _renderPaperwork(f) {
        const c = f.contract;
        if (!c) {
            return html`<span class="paperwork-missing">No contract record \u2014 cannot sign an SOW</span>`;
        }
        const summary = taxFormSummary(c);
        return html`
            ${c.legal_name ? html`<div class="paperwork">${c.legal_name}</div>` : ''}
            <div><span class="badge status-badge ${MSA_STATUS_COLORS[c.msa_status] || 'bg-secondary'}">MSA: ${MSA_STATUS_LABELS[c.msa_status] || c.msa_status}</span></div>
            <div class="paperwork-sub"><span class="${summary.className}">${summary.text}</span></div>
        `;
    }

    // The tax-form block, shared by the invite and contract forms the way
    // _renderMSAUpload is: `target` names the state property to write into.
    //
    // Which form applies follows from the contractor's lane and cannot be
    // derived from anything this app stores, so staff pick it. Offering only a
    // W-8BEN checkbox is what let a US contractor's W-9 be recorded as a W-8BEN
    // with a confident, invented expiry (issue #151).
    _renderTaxForm(target) {
        const current = this[target];
        const form = current.tax_form || TAX_FORM_W8BEN;
        const update = (patch) => {
            this[target] = { ...this[target], ...patch };
        };

        return html`
            <div class="mb-3">
                <label class="form-label">Tax form</label>
                <select class="form-select" @change="${(e) => update({ tax_form: e.target.value })}">
                    ${TAX_FORM_OPTIONS.map(
                        (o) =>
                            html`<option value="${o.value}" ?selected="${form === o.value}">${o.label}</option>`,
                    )}
                </select>
                <div class="form-text">
                    Picks the master services agreement they sign: the Canadian one goes with a
                    W-8BEN, the US one with a W-9.
                </div>
            </div>
            <div class="form-check mb-3">
                <input type="checkbox" class="form-check-input" id="${target}-tax-form-on-file"
                    .checked="${!!current.tax_form_on_file}"
                    @change="${(e) => update({ tax_form_on_file: e.target.checked })}" />
                <label class="form-check-label" for="${target}-tax-form-on-file">Tax form on file</label>
            </div>
            ${
                current.tax_form_on_file
                    ? html`
            <div class="mb-3">
                <label class="form-label">
                    Signed on ${requiresSigningDate(form) ? html`<span class="text-danger">*</span>` : ''}
                </label>
                <input type="date" class="form-control"
                    .value="${current.tax_form_signed_on || ''}"
                    @input="${(e) => update({ tax_form_signed_on: e.target.value })}" />
                <div class="form-text">
                    ${
                        requiresSigningDate(form)
                            ? 'Required \u2014 a W-8BEN expires on the last day of the third succeeding calendar year after signing.'
                            : 'Optional \u2014 a W-9 does not expire. It stays valid until their name, TIN, or entity classification changes.'
                    }
                </div>
            </div>
            `
                    : ''
            }
        `;
    }

    _renderContract() {
        const f = this._contractFor;
        const signedInApp = [MSA_AWAITING_COUNTERSIGNATURE, MSA_EXECUTED].includes(
            f.contract?.msa_status,
        );
        const set = (field) => (e) => {
            this._contract = { ...this._contract, [field]: e.target.value };
        };

        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._view = 'list';
            }}">&larr; Back to freelancers</a>
            <div class="page-header"><h4>Contract paperwork</h4></div>
            <p class="page-subtitle">
                ${f.email} — the legal name and MSA date appear in every Statement of Work this
                freelancer signs.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            <form class="form-panel" @submit="${this._saveContract}">
                ${
                    signedInApp
                        ? html`
                <div class="alert alert-light border small">
                    Their master services agreement is signed in the app, so the legal name and
                    MSA date come from it and can't be edited here.
                    ${f.contract?.msa_status === MSA_AWAITING_COUNTERSIGNATURE ? 'Void it from the MSA view to have them sign again.' : ''}
                </div>
                `
                        : html`
                <p class="form-hint">
                    Leave these blank and the freelancer signs their master services agreement in
                    the app. Fill them in only to record an MSA they signed outside it.
                </p>
                <div class="mb-3">
                    <label class="form-label">Legal name</label>
                    <input class="form-control"
                        .value="${this._contract.legal_name || ''}" @input="${set('legal_name')}" />
                    <div class="form-text">Exactly as it appears in the master services agreement.</div>
                </div>
                <div class="mb-3">
                    <label class="form-label">Master agreement reference</label>
                    <input class="form-control"
                        .value="${this._contract.msa_reference || ''}" @input="${set('msa_reference')}" />
                </div>
                <div class="mb-3">
                    <label class="form-label">Master agreement signed on</label>
                    <input type="date" class="form-control"
                        .value="${this._contract.msa_signed_on || ''}" @input="${set('msa_signed_on')}" />
                </div>
                ${this._renderMSAUpload('_contract')}
                `
                }
                ${this._renderTaxForm('_contract')}
                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                        ${this._saving ? 'Saving\u2026' : 'Save'}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" @click="${() => {
                        this._view = 'list';
                    }}">Cancel</button>
                </div>
            </form>
        `;
    }

    _renderExternalSOW() {
        const f = this._sowFor;
        const nextNumber = this._sows.reduce((max, s) => Math.max(max, s.number), 0) + 1;

        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._view = 'list';
            }}">&larr; Back to freelancers</a>
            <div class="page-header"><h4>Record a signed Statement of Work</h4></div>
            <p class="page-subtitle">
                ${f.email} — for an SOW signed on paper, outside the system. Recording it keeps this
                freelancer's numbering continuous, so the next SOW generated here does not repeat a
                number that is already signed and re-charge the onboarding fee.
            </p>

            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}

            ${
                this._sows.length > 0
                    ? html`<div class="mb-3">
                <div class="text-muted small">Already on file</div>
                <div>${this._sows.map((s) => html`<span class="badge bg-light text-dark me-1">No. ${s.number} — ${s.status}</span>`)}</div>
            </div>`
                    : nothing
            }

            <form class="form-panel" @submit="${this._saveExternalSOW}">
                <div class="mb-3">
                    <label class="form-label">SOW number</label>
                    <input type="number" min="1" class="form-control" placeholder="${nextNumber}"
                        .value="${this._sow.number || ''}"
                        @input="${(e) => {
                            this._sow = { ...this._sow, number: e.target.value };
                        }}" />
                    <div class="form-text">
                        Leave blank to use the next number (${nextNumber}). Set it to match what the
                        signed document itself says — a paper SOW No. 1 must be recorded as No. 1.
                    </div>
                </div>
                <div class="mb-3">
                    <label class="form-label">Signed document <span class="text-danger">*</span></label>
                    ${
                        this._sow.upload_id
                            ? html`<div class="form-text text-success">Attached${this._sow.file_name ? html` — ${this._sow.file_name}` : ''}</div>`
                            : html`<input type="file" class="form-control" accept="application/pdf"
                                @change="${(e) => this._uploadSOWDocument(e)}" />`
                    }
                    <div class="form-text">The freelancer can download this from their own SOW list.</div>
                </div>
                <div class="d-flex gap-2">
                    <button type="submit" class="btn btn-primary" ?disabled="${this._saving || !this._sow.upload_id}">
                        ${this._saving ? 'Saving…' : 'Record SOW'}
                    </button>
                    <button type="button" class="btn btn-outline-secondary" @click="${() => {
                        this._view = 'list';
                    }}">Cancel</button>
                </div>
            </form>
        `;
    }

    _renderList() {
        const pendingInvitations = this._invitations.filter((i) => i.status === 'pending');
        const pastInvitations = this._invitations.filter((i) => i.status !== 'pending');

        return html`
            <div class="page-header">
                <h4>Freelancers</h4>
                <button class="btn btn-primary btn-sm" @click="${this._startInvite}">+ Invite Freelancer</button>
            </div>
            <p class="page-subtitle">Manage freelance artists and their access.</p>

            ${
                this._loading
                    ? html`
                <div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>
            `
                    : html`
                <h6 class="section-title">Active Freelancers</h6>
                ${
                    this._freelancers.length > 0
                        ? html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Joined</th>
                                <th>Paperwork</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            ${this._freelancers.map(
                                (f) => html`
                                <tr>
                                    <td>${f.first_name || ''} ${f.last_name || ''} ${!f.first_name && !f.last_name ? f.username || '-' : ''}</td>
                                    <td>${f.email}</td>
                                    <td class="text-muted">${formatMediumDate(f.created_at)}</td>
                                    <td>${this._renderPaperwork(f)}</td>
                                    <td class="text-end">
                                        <button class="btn btn-outline-secondary btn-sm me-1" @click="${() => this._startContract(f)}">Contract</button>
                                        ${
                                            f.contract &&
                                            f.contract.msa_status !== MSA_AWAITING_SIGNATURE
                                                ? html`<button class="btn btn-sm me-1 ${f.contract.msa_status === MSA_AWAITING_COUNTERSIGNATURE ? 'btn-primary' : 'btn-outline-secondary'}"
                                                    @click="${() => (f.contract.msa_status === MSA_AWAITING_COUNTERSIGNATURE || f.contract.msa_status === MSA_EXECUTED ? this._startMSA(f) : this._downloadMSA(f))}">
                                                    ${f.contract.msa_status === MSA_AWAITING_COUNTERSIGNATURE ? 'Countersign MSA' : 'MSA'}
                                                </button>`
                                                : ''
                                        }
                                        <button class="btn btn-outline-secondary btn-sm me-1" @click="${() => this._startExternalSOW(f)}">Record SOW</button>
                                        ${
                                            this._removingId === f.id
                                                ? html`
                                            <span class="confirm-prompt">Revoke their access to D3 Assets?</span>
                                            <button class="btn btn-danger btn-sm" @click="${() => this._removeFreelancer(f)}">Remove</button>
                                            <button class="btn btn-outline-secondary btn-sm" @click="${() => {
                                                this._removingId = null;
                                            }}">Cancel</button>
                                        `
                                                : html`
                                            <button class="btn btn-outline-danger btn-sm" @click="${() => {
                                                this._removingId = f.id;
                                            }}">Remove</button>
                                        `
                                        }
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No active freelancers yet.</p>`
                }

                <h6 class="section-title">Pending Invitations</h6>
                ${
                    pendingInvitations.length > 0
                        ? html`
                    <table class="table table-hover">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Invited</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pendingInvitations.map(
                                (inv) => html`
                                <tr>
                                    <td>${inv.email}</td>
                                    <td class="text-muted">${formatMediumDate(inv.created_at)}</td>
                                    <td>
                                        <button class="btn btn-outline-danger btn-sm" @click="${() => this._revokeInvitation(inv)}">Revoke</button>
                                    </td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : html`<p class="text-muted">No pending invitations.</p>`
                }

                ${
                    pastInvitations.length > 0
                        ? html`
                    <h6 class="section-title">Past Invitations</h6>
                    <table class="table">
                        <thead>
                            <tr>
                                <th>Email</th>
                                <th>Status</th>
                                <th>Date</th>
                            </tr>
                        </thead>
                        <tbody>
                            ${pastInvitations.map(
                                (inv) => html`
                                <tr>
                                    <td>${inv.email}</td>
                                    <td>
                                        <span class="badge status-badge ${inv.status === 'accepted' ? 'bg-success' : 'bg-secondary'}">
                                            ${inv.status}
                                        </span>
                                    </td>
                                    <td class="text-muted">${formatMediumDate(inv.accepted_at || inv.created_at)}</td>
                                </tr>
                            `,
                            )}
                        </tbody>
                    </table>
                `
                        : nothing
                }
            `
            }
        `;
    }

    _renderInvite() {
        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._backToList();
            }}">
                <i class="bi bi-arrow-left"></i> Back to Freelancers
            </a>
            <h4 style="font-weight: 600; margin-bottom: 1.5rem;">Invite Freelancer</h4>
            <div class="form-panel">
                <form @submit="${this._submitInvite}">
                    <div class="mb-3">
                        <label class="form-label">Email Address *</label>
                        <input type="email" class="form-control" required placeholder="artist@example.com"
                            .value="${this._inviteEmail}"
                            @input="${(e) => (this._inviteEmail = e.target.value)}">
                    </div>
                    <h6 class="section-title">Contract paperwork</h6>
                    <p class="form-hint">
                        After accepting the invitation, the freelancer signs their master services
                        agreement in D3 Assets before anything else, and you countersign it here. The
                        tax form picks which agreement they sign: the US one (W-9) or the Canadian
                        one (W-8BEN).
                    </p>
                    ${this._renderTaxForm('_inviteContract')}

                    <div class="form-check mb-3">
                        <input type="checkbox" class="form-check-input" id="invite-signed-outside"
                            .checked="${!!this._inviteContract.signed_outside}"
                            @change="${(e) => {
                                this._inviteContract = {
                                    ...this._inviteContract,
                                    signed_outside: e.target.checked,
                                };
                            }}" />
                        <label class="form-check-label" for="invite-signed-outside">
                            They already signed a master services agreement outside the app
                        </label>
                    </div>
                    ${
                        this._inviteContract.signed_outside
                            ? html`
                    <div class="mb-3">
                        <label class="form-label">Legal name <span class="text-danger">*</span></label>
                        <input class="form-control" required placeholder="Exactly as it appears in the agreement"
                            .value="${this._inviteContract.legal_name || ''}"
                            @input="${(e) => {
                                this._inviteContract = {
                                    ...this._inviteContract,
                                    legal_name: e.target.value,
                                };
                            }}" />
                    </div>
                    <div class="mb-3">
                        <label class="form-label">Master agreement signed on <span class="text-danger">*</span></label>
                        <input type="date" class="form-control" required
                            .value="${this._inviteContract.msa_signed_on || ''}"
                            @input="${(e) => {
                                this._inviteContract = {
                                    ...this._inviteContract,
                                    msa_signed_on: e.target.value,
                                };
                            }}" />
                    </div>
                    ${this._renderMSAUpload('_inviteContract', true)}
                    `
                            : ''
                    }

                    <div class="mb-3">
                        <label class="form-label">Message</label>
                        <textarea class="form-control" rows="3" placeholder="Optional message to include in the invitation email"
                            .value="${this._inviteMessage}"
                            @input="${(e) => (this._inviteMessage = e.target.value)}"></textarea>
                    </div>
                    <div class="d-flex gap-2">
                        <button type="submit" class="btn btn-primary" ?disabled="${this._saving}">
                            ${this._saving ? 'Sending...' : 'Send Invitation'}
                        </button>
                        <button type="button" class="btn btn-outline-secondary" @click="${this._backToList}">Cancel</button>
                    </div>
                </form>
            </div>
        `;
    }

    _renderMSA() {
        const f = this._msaFor;
        const signing = this._msa?.signing;
        const party = (label, p) => html`
            <tr><th colspan="2" class="pt-3">${label}</th></tr>
            <tr><td class="text-muted">Signature</td><td>/s/ ${p.typed_signature}${p.title ? html`, ${p.title}` : ''}</td></tr>
            <tr><td class="text-muted">Signed at</td><td>${new Date(p.signed_at).toUTCString()}</td></tr>
            <tr><td class="text-muted">Account email</td><td>${p.email} (${p.email_verified ? 'verified' : 'not verified'})</td></tr>
            <tr><td class="text-muted">Signed in with</td><td>${p.auth_method}</td></tr>
            <tr><td class="text-muted">IP address</td><td>${p.ip || '\u2014'}</td></tr>
            <tr><td class="text-muted">User agent</td><td class="small">${p.user_agent || '\u2014'}</td></tr>
        `;
        const set = (field, value) => {
            this._countersign = { ...this._countersign, [field]: value };
        };
        return html`
            <a class="back-link" href="#" @click="${(e) => {
                e.preventDefault();
                this._view = 'list';
            }}">&larr; Back to freelancers</a>
            <div class="page-header"><h4>Master Services Agreement</h4></div>
            <p class="page-subtitle">${f.email}</p>
            ${
                !signing
                    ? html`<div class="text-center py-4"><div class="spinner-border spinner-border-sm text-primary" role="status"></div></div>`
                    : html`
                <p><span class="badge status-badge ${MSA_STATUS_COLORS[this._msa.status]}">${MSA_STATUS_LABELS[this._msa.status]}</span></p>
                <details class="mb-3">
                    <summary>The agreement as signed (document fingerprint <code class="small" title="${signing.document_sha256}">${shortFingerprint(signing.document_sha256)}</code>)</summary>
                    <pre class="small border rounded p-2 mt-2" style="white-space: pre-wrap; max-height: 24rem; overflow-y: auto;">${signing.rendered_text}</pre>
                </details>
                <table class="table table-sm small" style="max-width: 720px;">
                    <tbody>
                        ${party(`Contractor: ${signing.legal_name}`, signing.contractor)}
                        ${signing.company ? party('Company: Dice Driven Design LLC', signing.company) : ''}
                    </tbody>
                </table>
                ${
                    this._msa.status === MSA_EXECUTED && this._msa.has_document
                        ? html`<button class="btn btn-outline-primary btn-sm" @click="${() => this._downloadMSA(f)}"><i class="bi bi-download"></i> Download the signed agreement</button>`
                        : ''
                }
                ${
                    this._msa.status === MSA_AWAITING_COUNTERSIGNATURE
                        ? html`
                    <form class="form-panel mt-3" @submit="${this._countersignMSA}">
                        <h6 class="section-title">Countersign for Dice Driven Design LLC</h6>
                        <div class="form-check mb-3">
                            <input type="checkbox" class="form-check-input" id="countersign-consent"
                                .checked="${!!this._countersign.consent}" @change="${(e) => set('consent', e.target.checked)}" />
                            <label class="form-check-label small" for="countersign-consent">
                                I am authorized to sign this agreement for Dice Driven Design LLC, and I agree to sign it electronically.
                            </label>
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Your full name</label>
                            <input class="form-control" required .value="${this._countersign.typed_signature || ''}"
                                @input="${(e) => set('typed_signature', e.target.value)}" />
                        </div>
                        <div class="mb-3">
                            <label class="form-label">Your title</label>
                            <input class="form-control" required placeholder="CEO and Head of Engineering"
                                .value="${this._countersign.title || ''}" @input="${(e) => set('title', e.target.value)}" />
                        </div>
                        <div class="d-flex align-items-center gap-2 flex-wrap">
                            <button type="submit" class="btn btn-primary" ?disabled="${this._saving || !this._countersign.consent}">
                                ${this._saving ? 'Signing\u2026' : 'Countersign'}
                            </button>
                            <button type="button" class="btn btn-outline-danger" ?disabled="${this._saving}" @click="${this._voidMSA}">Void so they sign again</button>
                        </div>
                        <p class="form-hint mt-2">Countersigning executes the agreement. A signed PDF is stored and emailed to both of you.</p>
                    </form>
                `
                        : ''
                }
            `
            }
        `;
    }

    render() {
        return html`
            ${this._error ? html`<div class="alert alert-danger">${this._error}</div>` : ''}
            ${this._view === 'list' ? this._renderList() : ''}
            ${this._view === 'invite' ? this._renderInvite() : ''}
            ${this._view === 'contract' ? this._renderContract() : ''}
            ${this._view === 'external-sow' ? this._renderExternalSOW() : ''}
            ${this._view === 'msa' ? this._renderMSA() : ''}
        `;
    }
}

customElements.define('freelancers-view', FreelancersView);
